import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle, XCircle, Clock, Building2, MapPin, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function AdminClaims() {
  const { getAuthHeader } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [processing, setProcessing] = useState(null);

  useEffect(() => { fetchClaims(); }, [filter]);

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/claims?status=${filter}`, { headers: getAuthHeader() });
      setClaims(res.data.claims || []);
    } catch (err) {
      console.error("Failed to fetch claims:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (slug) => {
    setProcessing(slug);
    try {
      await axios.post(`${API}/admin/claims/${slug}/approve`, {}, { headers: getAuthHeader() });
      toast.success("Claim approved!");
      fetchClaims();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (slug) => {
    setProcessing(slug);
    try {
      await axios.post(`${API}/admin/claims/${slug}/reject`, {}, { headers: getAuthHeader() });
      toast.success("Claim rejected");
      fetchClaims();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    } finally {
      setProcessing(null);
    }
  };

  const statusColors = {
    pending: "text-[#F59E0B] bg-[#F59E0B]/10",
    approved: "text-[#10B981] bg-[#10B981]/10",
    rejected: "text-[#EF4444] bg-[#EF4444]/10",
  };

  return (
    <div className="space-y-6" data-testid="admin-claims">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Listing Claims</h1>
          <p className="text-[#94A3B8] mt-1">Review and approve business claim requests</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {["pending", "approved", "rejected"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
              filter === f ? "bg-[#7C3AED] text-white" : "text-[#94A3B8] bg-white/5 hover:bg-white/10"
            }`} data-testid={`claims-filter-${f}`}>
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : claims.length > 0 ? (
        <div className="space-y-3">
          {claims.map((claim, i) => (
            <motion.div key={claim.slug || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-[#0F172A] border border-white/10 rounded-xl p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="w-5 h-5 text-[#7C3AED]" />
                    <h3 className="text-white font-semibold">{claim.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[claim.claim_status] || ""}`}>
                      {claim.claim_status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                    {(claim.city || claim.state) && (
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{[claim.city, claim.state].filter(Boolean).join(", ")}</span>
                    )}
                    {claim.claimed_by_email && (
                      <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{claim.claimed_by_email}</span>
                    )}
                    {claim.category_name && (
                      <span className="text-[#7C3AED]">{claim.category_name}</span>
                    )}
                  </div>
                </div>
                {claim.claim_status === "pending" && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => handleApprove(claim.slug)}
                      disabled={processing === claim.slug} className="bg-[#10B981] hover:bg-[#059669]"
                      data-testid={`approve-${claim.slug}`}>
                      {processing === claim.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3 h-3 mr-1" /> Approve</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleReject(claim.slug)}
                      disabled={processing === claim.slug}
                      className="border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10"
                      data-testid={`reject-${claim.slug}`}>
                      <XCircle className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-[#0F172A] border border-white/10 rounded-xl p-12 text-center">
          <Clock className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No {filter} Claims</h2>
          <p className="text-[#94A3B8]">
            {filter === "pending" ? "No claims waiting for review." : `No ${filter} claims found.`}
          </p>
        </div>
      )}
    </div>
  );
}
